'use memo';

import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  DocumentTypes,
  DocumentTypesMap,
  SPECIAL_CHARACTERS,
  AustralianState,
  CampaignDetailModel,
  FormConfigType,
  MultiOptionButtonOption,
  StakeholderType,
  QldContractStage,
  NswContractStage,
} from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  useToast,
  useWindowSize,
  Breakpoints,
} from '@urbanx/agx.ui-components2';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { selectContract } from 'features/campaigns/campaignsReducer';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useFormLoader } from 'hooks/useFormLoader';
import { useSelectedContract } from 'hooks/useSelectedContract';
import { sortMultiOptionButtonOptionsByType } from 'utils/documentUtil';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { formatListOfStrings } from 'utils/formatString';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import FileList from '../Common/FileList';
import { Tabs } from 'types/Tabs';
import { getEnumValue } from 'helpers/enumHelper';
import {
  FormType,
  useCompleteCampaignStep,
  useGetUploadedFileLink,
  type Form as GeneratedForm,
} from '@urbanx/agx.campaigns.client';
import { openFileInNewTab } from 'utils/openFileInNewTab';

interface ContractSubmitPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  showDownload?: boolean;
  showSubmit?: boolean;
}

const contractSubmissionErrorMessage = 'Failed to start Contract submission';

const withCampaignFormConfig = (form: GeneratedForm) =>
  ({
    ...form,
    formConfigType: FormConfigType.Campaign,
  }) as unknown as Parameters<
    ReturnType<typeof useFormLoader>['loadFormWithConfig']
  >[0];

type ContractDocuments =
  | NonNullable<ReturnType<typeof useSelectedContract>>['documents']
  | undefined;

const createFileDownloadOptions = (
  documents: ContractDocuments,
  setDownloadFilePath: (filePath: string) => void
) => {
  const options: MultiOptionButtonOption[] = [];

  if (!documents || documents.length === 0) return options;

  documents.forEach(document => {
    if (document.documentType === DocumentTypes.SalesContract) return;

    options.push({
      text: DocumentTypesMap[
        document.documentType as keyof typeof DocumentTypesMap
      ],
      documentType: document.documentType,
      onClick: () => {
        if (!document.containerFilePath) return;

        setDownloadFilePath(document.containerFilePath);
      },
    });
  });

  const textCount = new Map();
  const uniqueDocumentList = options.map(item => {
    const count = textCount.get(item.text) || 0;
    textCount.set(item.text, count + 1);

    return {
      ...item,
      text: count > 0 ? `${item.text}-${count}` : item.text,
    };
  });

  const finalUniqueDocumentList = [
    ...new Map(uniqueDocumentList.map(item => [item.text, item])).values(),
  ] as MultiOptionButtonOption[];

  return [
    ...(sortMultiOptionButtonOptionsByType(
      finalUniqueDocumentList
    ) as MultiOptionButtonOption[]),
  ];
};

const ContractFileList = ({
  documents,
  showDownload,
  state,
  fileDownloadOptions,
}: {
  documents: ContractDocuments;
  showDownload: boolean;
  state: AustralianState;
  fileDownloadOptions: MultiOptionButtonOption[];
}) => {
  if (!documents || documents.length === 0 || !showDownload) return null;

  return (
    <FileList
      id="viewContractSubmissionPdfs"
      files={
        state === AustralianState.NSW
          ? fileDownloadOptions.filter(
              file => file.text !== DocumentTypesMap.TitleSearch
            )
          : fileDownloadOptions
      }
    />
  );
};

const ContractSubmitPanel = ({
  campaign,
  currentTab,
  showDownload = true,
  showSubmit = false,
}: ContractSubmitPanelProps) => {
  const dispatch = useAppDispatch();
  const contract = useSelectedContract();
  const { loadFormWithConfig } = useFormLoader();
  const { pushToast } = useToast();
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const [downloadFilePath, setDownloadFilePath] = useState<string>();
  const { data: downloadFileLink, isError: downloadFileLinkError } =
    useGetUploadedFileLink(downloadFilePath ?? '', !!downloadFilePath);
  const { completeCampaignStep, completeCampaignStepPending } =
    useCompleteCampaignStep({
      onSuccess: () => {},
      onError: () => {},
    });
  const {
    contractStage: stage,
    lastUpdate,
    buyers,
    documents,
    salePrice,
  } = contract || {};
  const {
    address,
    state,
    propertyImageUrls: { medium: imageUrl = null } = {},
  } = campaign;

  if (!stage) throw new Error('Contract stage is undefined');

  const enumType =
    state === AustralianState.QLD ? QldContractStage : NswContractStage;

  const contractStage = getEnumValue(enumType, stage) ?? stage;

  const headerDetail = `${showSubmit ? 'Last updated' : 'Submitted'} ${dayjs(
    lastUpdate
  ).format('DD/MM/YY')}`;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);

  const allBuyerNames = getAllBuyerNames(allBuyers);

  const screenSize = useWindowSize();

  const isDesktop = screenSize === Breakpoints.Desktop;

  useEffect(() => {
    if (!downloadFilePath || !downloadFileLink) return;

    openFileInNewTab(isDesktop, downloadFileLink);
    setDownloadFilePath(undefined);
  }, [downloadFilePath, downloadFileLink]);

  useEffect(() => {
    if (!downloadFileLinkError) return;

    setDownloadFilePath(undefined);
  }, [downloadFileLinkError]);

  const beginSubmission = async () => {
    const contractSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ContractSubmission &&
        form.state === campaign.state
    );

    if (
      !contractSubmissionForm ||
      !campaign.campaignId ||
      !contract?.contractId
    )
      return;

    try {
      const newForm = await completeCampaignStep({
        campaignId: campaign.campaignId,
        forcedCompletion: true,
        formId: contract.contractId,
      });

      if (!newForm) {
        pushToast({
          type: 'error',
          message: contractSubmissionErrorMessage,
        });
        return;
      }

      await loadFormWithConfig(withCampaignFormConfig(newForm));
    } catch (err) {
      console.error(err);
      pushToast({
        type: 'error',
        message: contractSubmissionErrorMessage,
      });
    }
  };

  const fileDownloadOptions = createFileDownloadOptions(
    documents,
    setDownloadFilePath
  );

  return (
    <>
      <FormDetailsHeader
        id={'contract-submit-panel-header'}
        formName="Contract"
        currentTab={currentTab}
        detail={headerDetail}
        onBack={() => dispatch(selectContract(null))}
      />

      <AgxSpacer fill contentAlignment="center" gap="spacing-xl">
        <AgxTypography
          id="contract-submit-panel-address"
          type="heading"
          size="2x-large"
          style={{ textAlign: 'center' }}
        >
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxTypography>
        <AgxTypography
          id="contract-submit-panel-buyers-sale-price"
          type="body"
          size="medium"
          style={{ textAlign: 'center' }}
        >
          {formatListOfStrings(allBuyerNames)} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
        </AgxTypography>
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          contentJustification="center"
          className="listing-imageCard"
        >
          <div
            style={{
              backgroundImage: `url("${imageUrl || placeHolderImage}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              aspectRatio: '2/1',
            }}
            className={`imageCard-image`}
          >
            <div className="image-tag">
              <AgxTypography
                id="contract-submit-panel-stage"
                type="label"
                size="medium"
                color="var(--brand-text-button-color)"
              >
                {contractStage}
              </AgxTypography>
            </div>
          </div>
        </AgxSpacer>
        <ContractFileList
          documents={documents}
          showDownload={showDownload}
          state={state}
          fileDownloadOptions={fileDownloadOptions}
        />
        {showSubmit && (
          <AgxButton
            id="startsubmission"
            variant="primary"
            size="large"
            text="Submit Contract"
            onClick={beginSubmission}
            disabled={completeCampaignStepPending}
          />
        )}
      </AgxSpacer>
    </>
  );
};

export default ContractSubmitPanel;
